import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { Transition } from '../../components/Transition';
import { Users } from '../../store/users/api/interfaces';
import { CompanyLicenses, BrokerLicenses } from '../../client/services';
import { getAttachmentAssetUrl, getAttachmentPreviewSrc } from '../../utils/url';
import { useAppSelector } from '../../store';
import moment from 'moment';
import Loader from '../../components/Loader';
import Label from '../../components/Label';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  actionUser: Users | undefined;
}

const CompanyDetailsModal: React.FC<IProps> = ({
  toggle,
  onClose,
  actionUser,
}) => {
  const [currentLicense, setCurrentLicense] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const isBroker = actionUser?.userRole === 'broker';

  useEffect(() => {
    if (toggle && actionUser?.id) {
      fetchDetails();
    }
  }, [toggle, actionUser?.id]);

  const fetchDetails = async () => {
    if (!actionUser?.id) return;

    try {
      setLoading(true);

      // Fetch current license (broker or company)
      const LicenseService = isBroker ? BrokerLicenses : CompanyLicenses;
      const idField = isBroker ? 'brokerId' : 'companyId';
      const licenses = await LicenseService.find({
        query: {
          [idField]: actionUser.id,
          isActive: true,
          $limit: 1,
        },
      });

      if (licenses?.data && licenses.data.length > 0) {
        setCurrentLicense(licenses.data[0]);
      } else {
        setCurrentLicense(null);
      }

      // Get attachments from user object
      if (actionUser.attachments && Array.isArray(actionUser.attachments)) {
        setAttachments(actionUser.attachments);
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      setCurrentLicense(null);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const getLicenseStatus = () => {
    if (!currentLicense) return null;

    const expirationDate = moment(currentLicense.expirationDate);
    const today = moment();
    const gracePeriodStart = moment(currentLicense.gracePeriodStartDate);

    if (today.isAfter(gracePeriodStart.clone().add(90, 'days'))) {
      return {
        type: 'error' as const,
        message: 'License expired and grace period ended. Account is suspended.',
        isSuspended: true,
      };
    } else if (today.isAfter(gracePeriodStart)) {
      const daysInGrace = today.diff(gracePeriodStart, 'days');
      return {
        type: 'warning' as const,
        message: `License expired. Grace period active (${90 - daysInGrace} days remaining).`,
        isSuspended: false,
      };
    } else if (today.isAfter(expirationDate)) {
      return {
        type: 'warning' as const,
        message: 'License expired. Grace period will start.',
        isSuspended: false,
      };
    } else {
      const daysUntilExpiry = expirationDate.diff(today, 'days');
      if (daysUntilExpiry <= 30) {
        return {
          type: 'warning' as const,
          message: `License expires in ${daysUntilExpiry} days.`,
          isSuspended: false,
        };
      }
      return {
        type: 'success' as const,
        message: `License valid until ${expirationDate.format('YYYY-MM-DD')}.`,
        isSuspended: false,
      };
    }
  };

  const status = getLicenseStatus();
  const suspensionDate = currentLicense && status?.isSuspended
    ? moment(currentLicense.gracePeriodStartDate).add(90, 'days').format('YYYY-MM-DD')
    : null;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="company-details-dialog"
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
        {isBroker ? 'Broker' : 'Company'} Details - {actionUser?.name}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Loader />
          </Box>
        ) : (
          <>
            {/* License Status Alert */}
            {status && (
              <Alert severity={status.type} sx={{ mb: 3 }}>
                {status.message}
              </Alert>
            )}

            {/* License Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  License Information
                </Typography>
                <Grid container spacing={2}>
                  {currentLicense ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Expiration Date:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {moment(currentLicense.expirationDate).format('YYYY-MM-DD')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Grace Period Start:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {currentLicense.gracePeriodStartDate
                            ? moment(currentLicense.gracePeriodStartDate).format('YYYY-MM-DD')
                            : '-'}
                        </Typography>
                      </Grid>
                      {currentLicense.gracePeriodStartDate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Grace Period Ends:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {moment(currentLicense.gracePeriodStartDate).add(90, 'days').format('YYYY-MM-DD')}
                          </Typography>
                        </Grid>
                      )}
                      {suspensionDate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Suspension Date:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="error">
                            {suspensionDate}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          License Status:
                        </Typography>
                        <Label
                          variant="ghost"
                          color={currentLicense.isActive ? 'success' : 'error'}
                        >
                          {currentLicense.isActive ? 'Active' : 'Inactive'}
                        </Label>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No license information available
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Documents/Attachments */}
            {attachments.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Documents
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {attachments.map((attachment) => {
                      const isImage = attachment.fileType?.split('/')[0] === 'image';
                      const isPdf = attachment.fileType === 'application/pdf' || 
                                   (attachment.fileType?.split('/')[0] === 'application' && 
                                    attachment.fileType?.split('/')[1] === 'pdf');
                      const assetUrl = getAttachmentAssetUrl(attachment, accessToken);
                      const previewSrc = getAttachmentPreviewSrc(attachment, accessToken);

                      return (
                        <Box
                          key={attachment.id}
                          sx={{
                            width: 120,
                            height: 120,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 2,
                            },
                          }}
                          onClick={() => {
                            if (assetUrl) {
                              window.open(assetUrl, '_blank');
                            }
                          }}
                        >
                          {isImage ? (
                            <img
                              src={previewSrc}
                              alt={attachment.fileName}
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px 4px 0 0',
                              }}
                            />
                          ) : isPdf ? (
                            <img
                              src="/static/pdf.png"
                              alt="PDF"
                              style={{
                                width: '60px',
                                height: '60px',
                              }}
                            />
                          ) : (
                            <img
                              src="/static/docket.png"
                              alt="Document"
                              style={{
                                width: '60px',
                                height: '60px',
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              px: 1,
                              textAlign: 'center',
                              fontSize: '0.7rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {attachment.documentType || 'Document'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDetailsModal;

